<template>
  <div class="trailers-slider-container" ref="sliderContainer">
    <!-- Steel-themed header with accent elements -->
    <div class="slider-header">
      <div class="steel-accent left"></div>
      <div class="steel-accent right"></div>
      
      <h2 class="section-title">
        <div class="title-accent top"></div>
        <span class="title-text">Our Trailers in Action</span>
        <div class="title-accent bottom"></div>
      </h2>
      <p class="section-subtitle">Looking good while working hard</p>
    </div>
    
    <!-- Industrial-themed slider with metal accents -->
    <div class="slider-wrapper" ref="sliderWrapper">
      <!-- Metallic corner elements -->
      <div class="corner-element top-left"></div>
      <div class="corner-element top-right"></div>
      <div class="corner-element bottom-left"></div>
      <div class="corner-element bottom-right"></div>
      
      <!-- Main slider area -->
      <div class="slider-viewport">
        <div 
          class="slider-track" 
          :style="{transform: `translateX(${-currentIndex * 100}%)`}"
          @touchstart="handleTouchStart"
          @touchmove="handleTouchMove"
          @touchend="handleTouchEnd"
        >
          <div 
            v-for="(image, index) in images" 
            :key="index" 
            class="slider-slide"
            @click="openLightbox(index)"
          >
            <div class="image-container">
              <img 
                :src="image.thumbnail" 
                :data-full="image.full"
                :alt="image.description || 'Trailer image'" 
                loading="lazy"
                class="slide-image"
              />
              <div class="image-overlay">
                <div class="image-info">
                  <span class="image-title">{{ image.title }}</span>
                  <span class="image-location" v-if="image.location">
                    <font-awesome-icon :icon="['fas', 'location-dot']" />
                    {{ image.location }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Steel-themed navigation controls -->
        <button 
          class="nav-button prev" 
          @click="prevSlide" 
          :disabled="currentIndex === 0"
          aria-label="Previous slide"
        >
          <div class="button-background"></div>
          <font-awesome-icon :icon="['fas', 'chevron-left']" />
        </button>
        
        <button 
          class="nav-button next" 
          @click="nextSlide" 
          :disabled="currentIndex === images.length - slidesVisible"
          aria-label="Next slide"
        >
          <div class="button-background"></div>
          <font-awesome-icon :icon="['fas', 'chevron-right']" />
        </button>
      </div>
      
      <!-- Indicator dots with metallic styling -->
      <div class="slider-indicators">
        <button 
          v-for="(_, index) in Math.ceil(images.length / slidesVisible)" 
          :key="index"
          class="indicator-dot"
          :class="{ active: Math.floor(currentIndex / slidesVisible) === index }"
          @click="goToPage(index)"
          :aria-label="`Go to page ${index + 1}`"
        >
          <div class="dot-inner"></div>
        </button>
      </div>
    </div>
    
    <!-- Decorative rivets/bolts in each corner for industrial feel -->
    <div class="rivet top-left"></div>
    <div class="rivet top-right"></div>
    
    <!-- Lightbox for enlarged image view -->
    <div 
      class="lightbox-overlay" 
      v-if="lightboxOpen"
      @click="closeLightbox" 
    >
      <div class="lightbox-container" @click.stop>
        <!-- Decorative corner accents -->
        <div class="corner-accent top-left"></div>
        <div class="corner-accent top-right"></div>
        <div class="corner-accent bottom-left"></div>
        <div class="corner-accent bottom-right"></div>
        
        <button class="close-button" @click="closeLightbox">
          <font-awesome-icon :icon="['fas', 'times']" />
        </button>
        
        <div class="lightbox-content">
          <div 
            class="lightbox-track"
            :style="{transform: `translateX(${-lightboxIndex * 100}%)`}"
          >
            <div 
              v-for="(image, index) in images" 
              :key="`lightbox-${index}`" 
              class="lightbox-slide"
            >
              <img 
                :src="image.full || image.thumbnail" 
                :alt="image.description || 'Trailer image'"
                class="lightbox-image" 
              />
            </div>
          </div>
          
          <div class="lightbox-info">
            <div class="flex flex-row w-full justify-center">
              <h3>{{ images[lightboxIndex]?.title || 'Trailer Image' }}</h3>
              <div class="location-tag" v-if="images[lightboxIndex]?.location">
                <font-awesome-icon :icon="['fas', 'location-dot']" />
                <span>{{ images[lightboxIndex].location }}</span>
              </div>
            </div>
            <p v-if="images[lightboxIndex]?.description">
              {{ images[lightboxIndex].description }}
            </p>
          </div>
          
          <button 
            class="lightbox-nav prev" 
            @click.stop="prevLightboxSlide" 
            :disabled="lightboxIndex === 0"
          >
            <font-awesome-icon :icon="['fas', 'chevron-left']" />
          </button>
          
          <button 
            class="lightbox-nav next" 
            @click.stop="nextLightboxSlide" 
            :disabled="lightboxIndex === images.length - 1"
          >
            <font-awesome-icon :icon="['fas', 'chevron-right']" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TrailersSlider',
  data() {
    return {
      currentIndex: 0,
      slidesVisible: 3, // Default for desktop
      lightboxOpen: false,
      lightboxIndex: 0,
      touchStartX: 0,
      touchEndX: 0,
      isAnimating: false,
      observer: null,
      isInView: false,
      images: [
        {
          thumbnail: '/img/trailers-slider/TrailersSlider1.jpg',
          full: '/img/trailers-slider/TrailersSlider1.jpg',
          title: 'Food Grade Tanker',
          description: 'Our premium food grade tanker delivering milk products in the Midwest.',
          location: 'Wisconsin'
        },
        {
          thumbnail: '/img/trailers-slider/TrailersSlider2.jpg',
          full: '/img/trailers-slider/TrailersSlider2.jpg',
          title: 'Chemical Trailer',
          description: 'Chemical transport trailer with enhanced safety features.',
          location: 'Texas'
        },
        {
          thumbnail: '/img/trailers-slider/TrailersSlider3.jpg',
          full: '/img/trailers-slider/TrailersSlider3.jpg',
          title: '3-A Sanitary Trailer',
          description: 'Sanitary trailer designed for the highest hygiene standards.',
          location: 'California'
        },
        {
          thumbnail: '/img/trailers-slider/TrailersSlider4.jpg',
          full: '/img/trailers-slider/TrailersSlider4.jpg',
          title: 'ASME Certified Tanker',
          description: 'ASME code tanker in operation at a processing facility.',
          location: 'Ohio'
        },
        {
          thumbnail: '/img/trailers-slider/TrailersSlider5.jpg',
          full: '/img/trailers-slider/TrailersSlider5.jpg',
          title: 'Double Conical Trailer',
          description: 'Double conical fertilizer trailer supporting agricultural operations.',
          location: 'Iowa'
        },
        {
          thumbnail: '/img/trailers-slider/TrailersSlider6.jpg',
          full: '/img/trailers-slider/TrailersSlider6.jpg',
          title: 'Custom Tanker',
          description: 'Custom-built tanker designed for specialized transport needs.',
          location: 'Michigan'
        },
        {
          thumbnail: '/img/trailers-slider/TrailersSlider7.jpg',
          full: '/img/trailers-slider/TrailersSlider7.jpg',
          title: '3A Sanitary',
          description: 'Custom-built tanker designed for specialized transport needs.',
          location: 'Michigan'
        },
        {
          thumbnail: '/img/trailers-slider/TrailersSlider8.jpg',
          full: '/img/trailers-slider/TrailersSlider8.jpg',
          title: 'DOT407 Chemical',
          description: 'Custom-built tanker designed for specialized transport needs.',
          location: 'Michigan'
        }
      ]
    };
  },
  mounted() {
    this.updateSlidesVisible();
    window.addEventListener('resize', this.updateSlidesVisible);
    
    // Initialize intersection observer for entrance animations
    this.setupIntersectionObserver();
    
    // Add keyboard navigation for lightbox
    window.addEventListener('keydown', this.handleKeyDown);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.updateSlidesVisible);
    window.removeEventListener('keydown', this.handleKeyDown);
    
    if (this.observer) {
      this.observer.disconnect();
    }
  },
  methods: {
    updateSlidesVisible() {
      const width = window.innerWidth;
      
      if (width < 640) {
        this.slidesVisible = 1; // Mobile
      } else if (width < 1024) {
        this.slidesVisible = 2; // Tablet
      } else {
        this.slidesVisible = 3; // Desktop
      }
      
      // Ensure current index is valid
      const maxIndex = Math.max(0, this.images.length - this.slidesVisible);
      this.currentIndex = Math.min(this.currentIndex, maxIndex);
    },
    
    nextSlide() {
      if (this.isAnimating) return;
      
      this.isAnimating = true;
      const maxIndex = Math.max(0, this.images.length - this.slidesVisible);
      this.currentIndex = Math.min(this.currentIndex + 1, maxIndex);
      
      setTimeout(() => {
        this.isAnimating = false;
      }, 500); // Match transition duration
    },
    
    prevSlide() {
      if (this.isAnimating) return;
      
      this.isAnimating = true;
      this.currentIndex = Math.max(0, this.currentIndex - 1);
      
      setTimeout(() => {
        this.isAnimating = false;
      }, 500); // Match transition duration
    },
    
    goToPage(pageIndex) {
      if (this.isAnimating) return;
      
      this.isAnimating = true;
      this.currentIndex = pageIndex * this.slidesVisible;
      
      setTimeout(() => {
        this.isAnimating = false;
      }, 500);
    },
    
    openLightbox(index) {
      this.lightboxIndex = index;
      this.lightboxOpen = true;
      // Add overflow: hidden to body to prevent background scrolling
      document.body.style.overflow = 'hidden';
    },
    
    closeLightbox() {
      this.lightboxOpen = false;
      // Restore scrolling
      document.body.style.overflow = '';
    },
    
    nextLightboxSlide() {
      if (this.lightboxIndex < this.images.length - 1) {
        this.lightboxIndex++;
      }
    },
    
    prevLightboxSlide() {
      if (this.lightboxIndex > 0) {
        this.lightboxIndex--;
      }
    },
    
    handleKeyDown(e) {
      if (!this.lightboxOpen) return;
      
      switch (e.key) {
        case 'ArrowRight':
          this.nextLightboxSlide();
          break;
        case 'ArrowLeft':
          this.prevLightboxSlide();
          break;
        case 'Escape':
          this.closeLightbox();
          break;
      }
    },
    
    handleTouchStart(e) {
      this.touchStartX = e.touches[0].clientX;
    },
    
    handleTouchMove(e) {
      this.touchEndX = e.touches[0].clientX;
    },
    
    handleTouchEnd() {
      const threshold = 50; // Minimum distance to trigger slide change
      const touchDiff = this.touchStartX - this.touchEndX;
      
      if (touchDiff > threshold) {
        // Swiped left, go to next slide
        this.nextSlide();
      } else if (touchDiff < -threshold) {
        // Swiped right, go to previous slide
        this.prevSlide();
      }
      
      // Reset touch values
      this.touchStartX = 0;
      this.touchEndX = 0;
    },
    
    setupIntersectionObserver() {
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              this.isInView = true;
              entry.target.classList.add('in-view');
            }
          });
        },
        { threshold: 0.1 }
      );
      
      if (this.$refs.sliderContainer) {
        this.observer.observe(this.$refs.sliderContainer);
      }
      
      if (this.$refs.sliderWrapper) {
        this.observer.observe(this.$refs.sliderWrapper);
      }
    }
  }
};
</script>

<style scoped>
.trailers-slider-container {
  width: 100%;
  max-width: 1400px;
  margin: 4rem auto;
  padding: 2rem 1rem;
  position: relative;
  opacity: 0;
  transform: translateY(30px);
  transition: opacity 1s ease-out, transform 1s cubic-bezier(0.16, 1, 0.3, 1);
  margin-bottom:5rem;
}

.trailers-slider-container.in-view {
  opacity: 1;
  transform: translateY(0);
}

/* Header styling */
.slider-header {
  position: relative;
  width: 100%;
  margin-bottom: 3rem;
  text-align: center;
  z-index: 1;
}

.steel-accent {
  position: absolute;
  height: 3px;
  background: linear-gradient(90deg, 
    rgba(183, 185, 186, 0.1),
    rgba(230, 232, 233, 0.7),
    rgba(183, 185, 186, 0.1)
  );
  z-index: 1;
}

.steel-accent.left {
  top: 50%;
  left: 0;
  width: 15%;
  transform: skewX(-30deg);
}

.steel-accent.right {
  top: 60%;
  right: 0;
  width: 15%;
  transform: skewX(30deg);
}

.section-title {
  position: relative;
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  display: inline-block;
}

.title-text {
  position: relative;
  z-index: 2;
  background: linear-gradient(135deg, white, #b7b9ba);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.3);
}

.title-accent {
  position: absolute;
  left: 0;
  height: 3px;
  width: 80px;
  background: linear-gradient(90deg, var(--color-primary), transparent);
}

.title-accent.top {
  top: 0;
}

.title-accent.bottom {
  bottom: 0;
  width: 120px;
}

.section-subtitle {
  font-size: 1.25rem;
  color: var(--color-secondary);
  max-width: 600px;
  margin: 0 auto;
  opacity: 0.8;
}

/* Slider wrapper styling */
.slider-wrapper {
  position: relative;
  width: 100%;
  padding: 20px;
  background: rgba(29, 30, 38, 0.4);
  border-radius: 12px;
  box-shadow: 
    0 15px 35px rgba(0, 0, 0, 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.05);
  overflow: hidden;
  opacity: 0;
  transform: translateY(40px);
  transition: opacity 1.2s ease-out, transform 1.2s cubic-bezier(0.16, 1, 0.3, 1);
  transition-delay: 0.2s;
}

.slider-wrapper.in-view {
  opacity: 1;
  transform: translateY(0);
}

/* Metallic corner elements */
.corner-element {
  position: absolute;
  width: 40px;
  height: 40px;
  z-index: 2;
}

.corner-element.top-left {
  top: 0;
  left: 0;
  border-top: 2px solid rgba(183, 185, 186, 0.7);
  border-left: 2px solid rgba(183, 185, 186, 0.7);
  border-top-left-radius: 8px;
}

.corner-element.top-right {
  top: 0;
  right: 0;
  border-top: 2px solid rgba(183, 185, 186, 0.7);
  border-right: 2px solid rgba(183, 185, 186, 0.7);
  border-top-right-radius: 8px;
}

.corner-element.bottom-left {
  bottom: 0;
  left: 0;
  border-bottom: 2px solid rgba(183, 185, 186, 0.7);
  border-left: 2px solid rgba(183, 185, 186, 0.7);
  border-bottom-left-radius: 8px;
}

.corner-element.bottom-right {
  bottom: 0;
  right: 0;
  border-bottom: 2px solid rgba(183, 185, 186, 0.7);
  border-right: 2px solid rgba(183, 185, 186, 0.7);
  border-bottom-right-radius: 8px;
}

/* Slider viewport and track */
.slider-viewport {
  position: relative;
  width: 100%;
  overflow: hidden;
  border-radius: 8px;
}

.slider-track {
  display: flex;
  transition: transform 0.5s cubic-bezier(0.25, 1, 0.5, 1);
  will-change: transform;
  margin-bottom: 20px;
}

.slider-slide {
  flex: 0 0 calc(100% / 3); /* Default for 3 slides visible */
  padding: 0 10px;
  cursor: pointer;
  transition: transform 0.3s ease;
  will-change: transform;
}

.slider-slide:hover {
  transform: translateY(-5px);
}

/* Image container styling */
.image-container {
  position: relative;
  width: 100%;
  height: 0;
  padding-bottom: 66.67%; /* 3:2 aspect ratio */
  overflow: hidden;
  border-radius: 8px;
  box-shadow: 
    0 10px 30px rgba(0, 0, 0, 0.3),
    0 4px 8px rgba(0, 0, 0, 0.2);
  border: 2px solid rgba(83, 85, 86, 0.3);
  transition: all 0.3s ease;
}

.slider-slide:hover .image-container {
  border-color: var(--color-primary);
  box-shadow: 
    0 15px 35px rgba(0, 0, 0, 0.4),
    0 6px 12px rgba(0, 0, 0, 0.3);
}

.slide-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
}

.image-container:hover .slide-image {
  transform: scale(1.05);
}

/* Image overlay and info */
.image-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.8), transparent);
  padding: 15px;
  opacity: 0;
  transform: translateY(20px);
  transition: all 0.3s ease;
}

.image-container:hover .image-overlay {
  opacity: 1;
  transform: translateY(0);
}

.image-info {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.image-title {
  font-weight: bold;
  color: white;
  font-size: 1rem;
}

.image-location {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  gap: 5px;
}

/* Navigation buttons */
.nav-button {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 40px;
  height: 40px;
  background: transparent;
  border: none;
  border-radius: 50%;
  color: white;
  font-size: 1.2rem;
  cursor: pointer;
  z-index: 10;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.nav-button:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.nav-button.prev {
  left: 10px;
}

.nav-button.next {
  right: 10px;
}

.button-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(143, 0, 0, 0.8);
  border-radius: 50%;
  transform: scale(0);
  transition: transform 0.3s cubic-bezier(0.25, 1, 0.5, 1);
  z-index: -1;
}

.nav-button:not(:disabled):hover .button-background {
  transform: scale(1);
}

/* Indicator dots */
.slider-indicators {
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-top: 20px;
}

.indicator-dot {
  width: 12px;
  height: 12px;
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  position: relative;
}

.dot-inner {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.3);
  transition: all 0.3s ease;
}

.indicator-dot:hover .dot-inner {
  background: rgba(255, 255, 255, 0.6);
}

.indicator-dot.active .dot-inner {
  background: var(--color-primary);
  box-shadow: 0 0 8px rgba(143, 0, 0, 0.6);
}

/* Decorative rivets/bolts */
.rivet {
  position: absolute;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: radial-gradient(circle at 30% 30%, #a8a9ad, #535556);
  box-shadow: 
    inset -2px -2px 6px rgba(0, 0, 0, 0.5),
    inset 2px 2px 6px rgba(255, 255, 255, 0.4);
  z-index: 5;
}

.rivet.top-left {
  top: 30px;
  left: 30px;
}

.rivet.top-right {
  top: 30px;
  right: 30px;
}

.rivet.bottom-left {
  bottom: 30px;
  left: 30px;
}

.rivet.bottom-right {
  bottom: 30px;
  right: 30px;
}

/* Lightbox styling */
.lightbox-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 9999; /* Increased z-index to ensure it's above everything */
  display: flex;
  align-items: center;
  justify-content: center;
  animation: fadeIn 0.3s ease;
}

.lightbox-container {
  position: relative;
  width: 90%;
  max-width: 1450px;
  max-height: 90vh; /* Changed from fixed height to max-height */
  background: rgba(29, 30, 38, 0.85);
  border-radius: 8px;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.5);
  overflow: hidden;
  animation: scaleIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  display: flex; /* Added flex display */
  flex-direction: column; /* Organize content vertically */
  padding-top:5rem;
}

/* Corner accents for lightbox */
.corner-accent {
  position: absolute;
  width: 30px;
  height: 30px;
  z-index: 5;
}

.corner-accent.top-left {
  top: 0;
  left: 0;
  border-top: 3px solid var(--color-primary);
  border-left: 3px solid var(--color-primary);
  border-top-left-radius: 8px;
}

.corner-accent.top-right {
  top: 0;
  right: 0;
  border-top: 3px solid var(--color-primary);
  border-right: 3px solid var(--color-primary);
  border-top-right-radius: 8px;
}

.corner-accent.bottom-left {
  bottom: 0;
  left: 0;
  border-bottom: 3px solid var(--color-primary);
  border-left: 3px solid var(--color-primary);
  border-bottom-left-radius: 8px;
}

.corner-accent.bottom-right {
  bottom: 0;
  right: 0;
  border-bottom: 3px solid var(--color-primary);
  border-right: 3px solid var(--color-primary);
  border-bottom-right-radius: 8px;
}

.lightbox-content {
  position: relative;
  width: 100%;
  flex: 1; /* Allow content to take available space */
  display: flex;
  flex-direction: column;
  overflow: hidden; /* Prevent content overflow */
}

.lightbox-track {
  display: flex;
  width: 100%;
  flex: 1; /* Take available space */
  transition: transform 0.5s cubic-bezier(0.25, 1, 0.5, 1);
  will-change: transform;
}

.lightbox-slide {
  flex: 0 0 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 20px; /* Reduced top/bottom padding */
}

.lightbox-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  border-radius: 4px;
}

.lightbox-info {
  padding: 10px 20px; /* Reduced padding */
  background: rgba(0, 0, 0, 0.5);
  color: white;
  position: relative; /* Changed from absolute to relative positioning */
  bottom: auto;
  left: auto;
  width: 100%;
  z-index: 5;
  display: flex; /* Added flex display */
  flex-wrap: wrap; /* Allow wrapping on small screens */
  align-items: center; /* Vertically center items */
  justify-content: space-between; /* Space between title and location */
}

.lightbox-info h3 {
  font-size: 1.2rem;
  margin-bottom: 0;
  margin-right: 1rem; /* Add space between title and location */
  color: white;
}

.lightbox-info p {
  width: 100%; /* Full width for description */
  font-size: 0.9rem;
  opacity: 0.8;
  margin-top: 0.5rem;
  margin-bottom: 0;
}

.location-tag {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 10px;
  background: rgba(143, 0, 0, 0.7);
  border-radius: 20px;
  font-size: 0.8rem;
  white-space: nowrap; /* Prevent wrapping inside tag */
}

/* Close button */
.close-button {
  position: absolute;
  top: 15px;
  right: 15px;
  width: 40px;
  height: 40px;
  background: var(--color-primary);
  border: none;
  border-radius: 50%;
  color: white;
  font-size: 1.2rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  transition: background 0.3s ease, transform 0.3s ease;
}

.close-button:hover {
  background: #a70000;
  transform: rotate(90deg);
}

.lightbox-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 50px;
  height: 50px;
  background: rgba(143, 0, 0, 0.7);
  border: none;
  border-radius: 50%;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  transition: all 0.3s ease;
}

.lightbox-nav:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.lightbox-nav:not(:disabled):hover {
  background: var(--color-primary);
  transform: translateY(-50%) scale(1.1);
}

.lightbox-nav.prev {
  left: 20px;
}

.lightbox-nav.next {
  right: 20px;
}

/* Animations */
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes scaleIn {
  from { transform: scale(0.9); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}

/* Responsive styling */
@media (max-width: 1024px) {
  .slider-slide {
    flex: 0 0 50%; /* 2 slides visible */
  }
  
  .section-title {
    font-size: 2rem;
  }
  
  .section-subtitle {
    font-size: 1rem;
  }
  
  .rivet {
    width: 12px;
    height: 12px;
  }
}

@media (max-width: 640px) {
  .slider-slide {
    flex: 0 0 100%; /* 1 slide visible */
  }
  
  .section-title {
    font-size: 1.5rem;
  }
  
  .section-subtitle {
    font-size: 0.9rem;
  }
  
  .slider-wrapper {
    padding: 15px;
  }
  
  .nav-button {
    width: 30px;
    height: 30px;
    font-size: 1rem;
  }
  
  .corner-element, 
  .rivet {
    display: none; /* Hide decorative elements on mobile */
  }
  
  .lightbox-nav {
    width: 40px;
    height: 40px;
    font-size: 1.2rem;
  }
  
  .lightbox-nav.prev {
    left: 10px;
  }
  
  .lightbox-nav.next {
    right: 10px;
  }
}
</style>
